// Sprint 17 A4: Contact detail with unified timeline + enrichment tabs
// C3: Inline editing with optimistic UI + undo
// C4: Delete with confirmation modal
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  buildCrmContactActionGroups,
  CRM_CONTACT_ACTION_GROUP_FIELD_KEYS,
  CRM_CONTACT_CHANNEL_FIELD_KEYS,
  type CrmContactActionGroup,
} from "@ease/crm/contact-action-groups";
import {
  buildCrmContactAssessmentResults,
  CRM_CONTACT_ASSESSMENT_RESULT_FIELD_KEYS,
} from "@ease/crm/contact-assessment-results";
import {
  buildCrmContactHealthSnapshot,
  CRM_CONTACT_HEALTH_SNAPSHOT_FIELD_KEYS,
} from "@ease/crm/contact-health-snapshot";
import {
  buildCrmContactProgressSnapshot,
  CRM_CONTACT_PROGRESS_SNAPSHOT_FIELD_KEYS,
} from "@ease/crm/contact-progress-snapshot";
import {
  buildCrmContactInteractionTimeline,
  CRM_CONTACT_INTERACTION_TIMELINE_FIELD_KEYS,
} from "@ease/crm/contact-interaction-timeline";
import {
  buildCrmContactIntakeAnswers,
  CRM_CONTACT_INTAKE_ANSWER_FIELD_KEYS,
} from "@ease/crm/contact-intake-answers";
import {
  buildCrmContactOperatorBrief,
  CRM_CONTACT_OPERATOR_BRIEF_FIELD_KEYS,
} from "@ease/crm/contact-operator-brief";
import {
  buildCrmContactRecommendationHints,
  CRM_CONTACT_RECOMMENDATION_HINT_FIELD_KEYS,
} from "@ease/crm/contact-recommendation-hints";
import {
  buildCrmProfileSections,
} from "@ease/crm/profile-sections";
import { useContact, updateContact, deleteContact } from "@/hooks/use-contacts";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import { useTaskList } from "@/hooks/use-tasks";
import { useCrmStageSuggestion, useCrmSummarize } from "@/hooks/use-crm-timeline";
import { useCrmHealthScore } from "@/hooks/use-crm-health-score";
import {
  useContactScoreBreakdown,
} from "@/hooks/use-crm-contact-score";
import { scanCrmContactForDuplicates, type DuplicateScanResult } from "@/hooks/use-crm-duplicates";
import { UnifiedTimeline } from "@/components/crm/unified-timeline";
import { SendFatigueTimeline } from "@/components/crm/send-fatigue-timeline";
import { EnrichmentPanel } from "@/components/crm/enrichment-panel";
import { HealthScorePanel } from "@/components/crm/health-score-panel";
import { RelatedTaskSnapshotCard } from "@/components/crm/related-task-snapshot-card";
import { CrmAssessmentResultsCard } from "@/components/crm/assessment-results-card";
import { CrmInteractionTimelineCard } from "@/components/crm/interaction-timeline-card";
import { CrmIntakeAnswersCard } from "@/components/crm/intake-answers-card";
import {
  CrmHealthSnapshotCard,
  CrmProgressSnapshotCard,
} from "@/components/crm/contact-snapshot-cards";
import { CrmOperatorBriefCard } from "@/components/crm/operator-brief-card";
import { CrmRecommendationHintsCard } from "@/components/crm/recommendation-hints-card";
import { CrmContactActionGroupsCard } from "@/components/crm/contact-action-groups-card";
import {
  buildCrmContactChannelActionGroup,
  buildCrmContactHeaderActions,
} from "@/components/crm/contact-action-builders";
import {
  getSafeCrmContactCustomFields,
  getSafeCrmContactTags,
} from "@/components/crm/contact-value-safety";
import { CrmContactHeaderSummary } from "@/components/crm/contact-header-summary";
import { CrmContactDeleteConfirmDialog } from "@/components/crm/contact-delete-confirm-dialog";
import {
  buildCrmContactOwnerCustomFields,
  CrmContactOwnerAssignmentCard,
  formatCrmOwnerMemberLabel,
  getCrmContactOwnerId,
  getCrmContactOwnerLabel,
} from "@/components/crm/contact-owner-assignment-card";
import {
  CrmContactDetailFactsList,
  CrmContactDetailTabs,
} from "@/components/crm/contact-detail-controls";
import {
  buildCrmContactQuickInfoItems,
  CrmContactQuickInfoGrid,
} from "@/components/crm/contact-quick-info-grid";
import {
  parseCrmContactAiSuggestions,
  type CrmContactAiSuggestion,
  type CrmContactAiSuggestionStatus,
} from "@/components/crm/contact-ai-suggestions-card";
import { CrmContactAiInsightsPanel } from "@/components/crm/contact-ai-insights-panel";
import { CrmContactTagsEditor } from "@/components/crm/contact-tags-editor";
import { CrmContactDuplicateScanCard } from "@/components/crm/contact-duplicate-scan-card";
import {
  CrmContactActivityLogCard,
  getDefaultCrmActivityTimestamp,
  type CrmContactActivityLogDraft,
} from "@/components/crm/contact-activity-log-card";
import {
  createCrmBookingRuleOverrideDraft,
  CrmContactBookingOverridesCard,
  type CrmBookingRuleOverride,
  type CrmBookingRuleOverrideDraft,
  type CrmBookingRuleServiceOption,
} from "@/components/crm/contact-booking-overrides-card";
import { CrmLeadScoreEvidenceCard } from "@/components/crm/lead-score-evidence-card";
import {
  CrmCommunicationMemoryCard,
  type CrmCommunicationMemoryDisplay,
} from "@/components/crm/communication-memory-card";
import { CrmRelationshipMap } from "@/components/crm/relationship-map";
import {
  CrmSourceAttributionCard,
  type CrmContactAttributionSummary,
} from "@/components/crm/source-attribution-card";
import {
  CrmAdditionalProfileFieldsCard,
  CrmProfileSectionsCard,
} from "@/components/crm/profile-sections-card";
import { SlideOverPanel } from "@/components/crm/slide-over-panel";
import { Skeleton } from "@/components/skeleton";
import { PropertyAssetTab } from "./property-asset-tab";
import { apiFetch } from "@/lib/api-client";
import { useApi, useApiMutation } from "@/hooks/use-api";
import { sendAiFeedbackSignal } from "@/lib/ai-feedback";
import { formatDate } from "@/lib/i18n";

interface ContactDetailProps {
  contactId: string;
  onClose: () => void;
  onDeleted?: () => void;
}

interface AiCompleteResponse {
  content?: string;
}

const OPEN_CONTACT_TASK_STATUSES = new Set(["pending", "in_progress"]);
const CONTACT_DETAIL_TABS = [
  { value: "details", label: "Details" },
  { value: "timeline", label: "Timeline" },
  { value: "relationships", label: "Relationships" },
  { value: "enrichment", label: "Enrichment" },
  { value: "health", label: "Health" },
  { value: "properties", label: "Properties" },
] as const;

type ContactDetailTab = (typeof CONTACT_DETAIL_TABS)[number]["value"];

export function ContactDetail({ contactId, onClose, onDeleted }: ContactDetailProps) {
  const { contact, isLoading, error } = useContact(contactId);
  const [activeTab, setActiveTab] = useState<ContactDetailTab>("details");
  const [tagInput, setTagInput] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [visibleTags, setVisibleTags] = useState<string[]>([]);
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const [tagMutationError, setTagMutationError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [activityDraft, setActivityDraft] = useState<CrmContactActivityLogDraft>(() => ({
    type: "note",
    subject: "",
    body: "",
    performedAt: getDefaultCrmActivityTimestamp(),
  }));
  const [isLoggingActivity, setIsLoggingActivity] = useState(false);
  const [activityFeedback, setActivityFeedback] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [duplicateScanResult, setDuplicateScanResult] = useState<DuplicateScanResult | null>(null);
  const [duplicateScanError, setDuplicateScanError] = useState<string | null>(null);
  const [isScanningDuplicates, setIsScanningDuplicates] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [aiSuggestions, setAiSuggestions] = useState<CrmContactAiSuggestion[]>([]);
  const [aiSuggestionStatuses, setAiSuggestionStatuses] = useState<Record<string, CrmContactAiSuggestionStatus>>({});
  const [aiSuggestionError, setAiSuggestionError] = useState<string | null>(null);
  const [bookingOverrides, setBookingOverrides] = useState<CrmBookingRuleOverride[]>([]);
  const [bookingServices, setBookingServices] = useState<CrmBookingRuleServiceOption[]>([]);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [ownerDraftId, setOwnerDraftId] = useState("");
  const [savedOwnerId, setSavedOwnerId] = useState("");
  const [ownerDisplayLabel, setOwnerDisplayLabel] = useState<string | null>(null);
  const [isSavingOwner, setIsSavingOwner] = useState(false);
  const [ownerMutationFeedback, setOwnerMutationFeedback] = useState<string | null>(null);
  const [ownerMutationError, setOwnerMutationError] = useState<string | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<CrmBookingRuleOverrideDraft>(() => createCrmBookingRuleOverrideDraft());
  const { summarize, isLoading: isSummarizing, error: summaryError } = useCrmSummarize(contactId);
  const {
    data: stageSuggestion,
    isLoading: isStageSuggestionLoading,
    error: stageSuggestionError,
    fetchSuggestion,
  } = useCrmStageSuggestion(contactId);
  const {
    current: healthCurrent,
    history: healthHistory,
    trendDirection,
  } = useCrmHealthScore(contactId);
  const {
    data: scoreBreakdown,
    meta: scoreBreakdownMeta,
    isLoading: isScoreBreakdownLoading,
    error: scoreBreakdownError,
  } = useContactScoreBreakdown(contactId);
  const {
    data: organizationMembers,
    isLoading: areOrganizationMembersLoading,
    error: organizationMembersError,
  } = useOrganizationMembers();
  const {
    tasks: contactTasks,
    isLoading: isContactTasksLoading,
    error: contactTasksError,
  } = useTaskList({ personId: contactId, limit: 10 });
  const communicationMemory = useApi<CrmCommunicationMemoryDisplay>(
    `/crm/people/${contactId}/communication-memory`,
    { enabled: !!contactId },
  );
  const attributionSummary = useApi<CrmContactAttributionSummary>(
    `/crm/people/${contactId}/attribution-summary`,
    { enabled: !!contactId },
  );
  const memberOptions = organizationMembers ?? [];
  const memberLabelById = useMemo(
    () => new Map(memberOptions.map((member) => [member.id, formatCrmOwnerMemberLabel(member)])),
    [memberOptions],
  );
  const aiSuggestionMutation = useApiMutation<{
    prompt: string;
    featureKey: string;
    messages: Array<{ role: "system" | "user"; content: string }>;
  }, AiCompleteResponse>("/crm/ai/complete");
  const contactCustomFields = getSafeCrmContactCustomFields(contact?.customFields);
  const contactOwnerId = getCrmContactOwnerId(contact, contactCustomFields);
  const contactAssigneeLabel = getCrmContactOwnerLabel(contact, contactCustomFields, memberLabelById);
  const contactOperatorBrief = buildCrmContactOperatorBrief(contactCustomFields);
  const contactAssessmentResults = buildCrmContactAssessmentResults(contactCustomFields);
  const contactHealthSnapshot = buildCrmContactHealthSnapshot(contactCustomFields);
  const contactProgressSnapshot = buildCrmContactProgressSnapshot(contactCustomFields);
  const contactInteractionTimeline = buildCrmContactInteractionTimeline(contactCustomFields);
  const contactIntakeAnswers = buildCrmContactIntakeAnswers(contactCustomFields);
  const contactRecommendationHints = buildCrmContactRecommendationHints(contactCustomFields);
  const contactProfileSections = buildCrmProfileSections(contactCustomFields, {
    excludedKeys: [
      ...CRM_CONTACT_ACTION_GROUP_FIELD_KEYS,
      ...CRM_CONTACT_CHANNEL_FIELD_KEYS,
      ...CRM_CONTACT_ASSESSMENT_RESULT_FIELD_KEYS,
      ...CRM_CONTACT_HEALTH_SNAPSHOT_FIELD_KEYS,
      ...CRM_CONTACT_PROGRESS_SNAPSHOT_FIELD_KEYS,
      ...CRM_CONTACT_INTERACTION_TIMELINE_FIELD_KEYS,
      ...CRM_CONTACT_INTAKE_ANSWER_FIELD_KEYS,
      ...CRM_CONTACT_OPERATOR_BRIEF_FIELD_KEYS,
      ...CRM_CONTACT_RECOMMENDATION_HINT_FIELD_KEYS,
    ],
  });
  const contactActionGroups = [
    buildCrmContactChannelActionGroup(contact, contactCustomFields),
    ...buildCrmContactActionGroups(contactCustomFields),
  ].filter((group): group is CrmContactActionGroup => Boolean(group));
  const contactHeaderActions = buildCrmContactHeaderActions(contactActionGroups);
  const isOwnerDraftKnown = !ownerDraftId || memberOptions.some((member) => member.id === ownerDraftId);
  const hasOwnerDraftChanged = ownerDraftId !== savedOwnerId;
  const visibleOwnerLabel = ownerDisplayLabel ?? contactAssigneeLabel;
  const contactQuickInfoItems = buildCrmContactQuickInfoItems({
    phone: contact?.phone,
    lifecycleStage: contact?.lifecycleStage,
    leadScore: contact?.leadScore ?? null,
    ownerLabel: visibleOwnerLabel,
    source: contact?.source,
  });

  useEffect(() => {
    setVisibleTags(getSafeCrmContactTags(contact?.tags));
  }, [contact]);

  useEffect(() => {
    const nextOwnerId = contactOwnerId ?? "";
    setOwnerDraftId(nextOwnerId);
    setSavedOwnerId(nextOwnerId);
    setOwnerDisplayLabel(contactAssigneeLabel);
    setOwnerMutationFeedback(null);
    setOwnerMutationError(null);
  }, [contactId, contactOwnerId, contactAssigneeLabel]);

  useEffect(() => {
    let cancelled = false;
    if (!contactId) {
      setBookingOverrides([]);
      setBookingServices([]);
      return;
    }

    Promise.all([
      apiFetch<{ data: CrmBookingRuleOverride[] }>(`/crm/bookings/rules-overrides?personId=${encodeURIComponent(contactId)}`),
      apiFetch<{ data: Array<{ id: string; name: string }> }>("/crm/bookings/services/intake"),
    ])
      .then(([overrideResponse, serviceResponse]) => {
        if (cancelled) return;
        setBookingOverrides(Array.isArray(overrideResponse.data) ? overrideResponse.data : []);
        setBookingServices(
          Array.isArray(serviceResponse.data)
            ? serviceResponse.data.map((service) => ({ id: service.id, name: service.name }))
            : [],
        );
        setOverrideError(null);
      })
      .catch((caughtError) => {
        if (!cancelled) {
          setOverrideError(caughtError instanceof Error ? caughtError.message : "Failed to load booking overrides");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contactId]);

  // C3: Inline edit handler
  const handleInlineEdit = useCallback(async (field: string, value: string) => {
    if (!contact) return;
    try {
      await updateContact(contactId, { [field]: value });
    } catch {
      // Revert happens on next refetch
    }
  }, [contactId, contact]);

  // C4: Delete handler
  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteContact(contactId);
      onClose();
      onDeleted?.();
    } catch {
      setIsDeleting(false);
    }
  }, [contactId, onClose, onDeleted]);

  const handleGenerateSummary = useCallback(async () => {
    const result = await summarize();
    setSummary(result?.summary ?? null);
  }, [summarize]);

  const handleRemoveTag = useCallback(async (tag: string) => {
    const previousTags = visibleTags;
    setRemovingTag(tag);
    setTagMutationError(null);
    setVisibleTags((current) => current.filter((entry) => entry !== tag));

    try {
      await apiFetch(`/crm/people/${contactId}/tags/${encodeURIComponent(tag)}`, {
        method: "DELETE",
      });
    } catch {
      setVisibleTags(previousTags);
      setTagMutationError(`Failed to remove ${tag}.`);
    } finally {
      setRemovingTag(null);
    }
  }, [contactId, visibleTags]);

  const handleAddTag = useCallback(async () => {
    const tag = tagInput.trim();
    if (!tag) return;
    try {
      await apiFetch(`/crm/people/${contactId}/tags`, {
        method: "POST",
        body: JSON.stringify({ tags: [tag] }),
      });
      setTagInput("");
    } catch {
      // Tag add is non-critical.
    }
  }, [contactId, tagInput]);

  const handleSaveOwner = useCallback(async () => {
    if (!contact) return;

    const selectedMember = memberOptions.find((member) => member.id === ownerDraftId);
    const owner = ownerDraftId
      ? {
        assigneeId: ownerDraftId,
        assigneeLabel: selectedMember ? formatCrmOwnerMemberLabel(selectedMember) : ownerDraftId,
      }
      : null;

    setIsSavingOwner(true);
    setOwnerMutationError(null);
    setOwnerMutationFeedback(null);

    try {
      await apiFetch(`/crm/people/${contactId}`, {
        method: "PATCH",
        body: JSON.stringify({
          customFields: buildCrmContactOwnerCustomFields(contactCustomFields, owner),
        }),
      });
      setSavedOwnerId(ownerDraftId);
      setOwnerDisplayLabel(owner?.assigneeLabel ?? "Unassigned");
      setOwnerMutationFeedback(owner ? `Owner updated to ${owner.assigneeLabel}.` : "Owner cleared.");
    } catch (caughtError) {
      setOwnerMutationError(caughtError instanceof Error ? caughtError.message : "Failed to update owner.");
    } finally {
      setIsSavingOwner(false);
    }
  }, [contact, contactCustomFields, contactId, memberOptions, ownerDraftId]);

  const handleLogActivity = useCallback(async () => {
    setActivityFeedback(null);
    setActivityError(null);
    setIsLoggingActivity(true);

    try {
      await apiFetch(`/crm/people/${contactId}/activities`, {
        method: "POST",
        body: JSON.stringify({
          type: activityDraft.type,
          subject: activityDraft.subject.trim() || null,
          body: activityDraft.body.trim() || null,
          performedAt: new Date(activityDraft.performedAt).toISOString(),
        }),
      });
      setActivityDraft((current) => ({
        ...current,
        subject: "",
        body: "",
        performedAt: getDefaultCrmActivityTimestamp(),
      }));
      setActivityFeedback("Activity logged and timeline refreshed.");
      setTimelineRefreshKey((current) => current + 1);
    } catch (caughtError) {
      setActivityError(caughtError instanceof Error ? caughtError.message : "Failed to log activity.");
    } finally {
      setIsLoggingActivity(false);
    }
  }, [activityDraft, contactId]);

  const handleDuplicateScan = useCallback(async () => {
    setIsScanningDuplicates(true);
    setDuplicateScanError(null);
    try {
      const result = await scanCrmContactForDuplicates(contactId);
      setDuplicateScanResult(result);
    } catch (caughtError) {
      setDuplicateScanResult(null);
      setDuplicateScanError(caughtError instanceof Error ? caughtError.message : "Failed to scan for duplicates");
    } finally {
      setIsScanningDuplicates(false);
    }
  }, [contactId]);

  const beginOverrideEdit = useCallback((override?: CrmBookingRuleOverride) => {
    setOverrideDraft(createCrmBookingRuleOverrideDraft(override));
  }, []);

  const handleSaveOverride = useCallback(async () => {
    setIsSavingOverride(true);
    setOverrideError(null);
    try {
      const payload = {
        personId: contactId,
        serviceId: overrideDraft.serviceId || null,
        customLateCancelHours: overrideDraft.customLateCancelHours === "" ? null : Number(overrideDraft.customLateCancelHours),
        emergencyPassesRemaining: overrideDraft.emergencyPassesRemaining === "" ? null : Number(overrideDraft.emergencyPassesRemaining),
        isVip: overrideDraft.isVip,
        notes: overrideDraft.notes.trim() || null,
      };

      const response = overrideDraft.id
        ? await apiFetch<{ data: CrmBookingRuleOverride }>(`/crm/bookings/rules-overrides/${overrideDraft.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        })
        : await apiFetch<{ data: CrmBookingRuleOverride }>("/crm/bookings/rules-overrides", {
          method: "POST",
          body: JSON.stringify(payload),
        });

      setBookingOverrides((current) => {
        const next = current.filter((entry) => entry.id !== response.data.id);
        return [...next, response.data];
      });
      beginOverrideEdit();
    } catch (caughtError) {
      setOverrideError(caughtError instanceof Error ? caughtError.message : "Failed to save booking override");
    } finally {
      setIsSavingOverride(false);
    }
  }, [beginOverrideEdit, contactId, overrideDraft]);

  const handleDeleteOverride = useCallback(async (overrideId: string) => {
    try {
      await apiFetch(`/crm/bookings/rules-overrides/${overrideId}`, { method: "DELETE" });
      setBookingOverrides((current) => current.filter((entry) => entry.id !== overrideId));
      if (overrideDraft.id === overrideId) {
        beginOverrideEdit();
      }
    } catch (caughtError) {
      setOverrideError(caughtError instanceof Error ? caughtError.message : "Failed to delete booking override");
    }
  }, [beginOverrideEdit, overrideDraft.id]);

  const displayName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || "Unknown"
    : "Unknown";
  const contactHeaderSignals = contact?.crmSummarySignals?.slice(0, 5) ?? [];
  const openContactTasks = contactTasks.filter((task) => OPEN_CONTACT_TASK_STATUSES.has(task.status));
  const visibleContactTasks = openContactTasks.slice(0, 5);
  const suggestedStage = typeof stageSuggestion?.suggestedStage === "string"
    ? stageSuggestion.suggestedStage
    : null;
  const suggestedConfidence = typeof stageSuggestion?.confidence === "number"
    ? stageSuggestion.confidence
    : null;
  const communicationContext = communicationMemory.data?.items ?? [];
  const contactAiInsightsError = summaryError ?? stageSuggestionError ?? aiSuggestionError ?? aiSuggestionMutation.error;

  const handleGenerateAiSuggestions = useCallback(async () => {
    if (!contact) return;

    setAiSuggestionError(null);
    const response = await aiSuggestionMutation.mutate({
      prompt: `Generate three next best actions for ${displayName}.`,
      featureKey: "crm.assistant.complete",
      messages: [
        {
          role: "system",
          content: [
            "Return exactly three next-best-action suggestions as strict JSON array.",
            "Each item must include title, action, and reasoning.",
            "Do not return markdown or explanatory text outside the JSON array.",
            "This output is AI-generated and will be reviewed by a human before any action is taken.",
          ].join(" "),
        },
        {
          role: "user",
          content: JSON.stringify({
            contact: {
              id: contact.id,
              name: displayName,
              email: contact.email,
              lifecycleStage: contact.lifecycleStage,
              leadScore: contact.leadScore,
              source: contact.source,
              tags: getSafeCrmContactTags(contact.tags),
              lastActivityAt: contact.lastActivityAt,
            },
            communicationMemory: communicationContext,
          }),
        },
      ],
    });

    const parsed = parseCrmContactAiSuggestions(response?.content ?? "");
    if (parsed.length === 0) {
      setAiSuggestions([]);
      setAiSuggestionStatuses({});
      setAiSuggestionError("AI could not generate actionable next steps from the current contact data.");
      return;
    }

    setAiSuggestions(parsed);
    setAiSuggestionStatuses({});
  }, [aiSuggestionMutation, communicationContext, contact, displayName]);

  const handleAiSuggestionDecision = useCallback(async (
    suggestion: CrmContactAiSuggestion,
    index: number,
    action: CrmContactAiSuggestionStatus,
  ) => {
    if (!contact) return;

    try {
      await sendAiFeedbackSignal({
        signalType: `crm.contact_suggestion.${action}`,
        aiOutputId: `${contact.id}:contact-suggestion:${index}`,
        aiOutput: {
          title: suggestion.title,
          action: suggestion.action,
          reasoning: suggestion.reasoning,
        },
        humanAction: {
          action: action === "accepted" ? "accept" : "dismiss",
        },
        context: {
          touchpoint: "crm.contact_detail.ai_suggestions",
          contactId: contact.id,
        },
      });
      setAiSuggestionStatuses((current) => ({
        ...current,
        [suggestion.title]: action,
      }));
      setAiSuggestionError(null);
    } catch {
      setAiSuggestionError("The suggestion was reviewed, but the AI feedback signal could not be recorded.");
    }
  }, [contact]);

  if (isLoading) {
    return (
      <SlideOverPanel onClose={onClose}>
        <div className="space-y-4 p-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </SlideOverPanel>
    );
  }

  if (error || !contact) {
    return (
      <SlideOverPanel onClose={onClose}>
        <div className="p-6 text-center">
          <p className="text-sm text-[var(--color-destructive)]">{error ?? "Contact not found"}</p>
        </div>
      </SlideOverPanel>
    );
  }

  return (
    <SlideOverPanel onClose={onClose}>
      {/* Header */}
      <div className="border-b border-[var(--color-border)] p-6">
        <CrmContactHeaderSummary
          displayName={displayName}
          email={contact.email}
          firstName={contact.firstName}
          lastName={contact.lastName}
          leadScore={contact.leadScore}
          healthScore={healthCurrent?.compositeScore ?? null}
          momentumDirection={trendDirection}
          actions={contactHeaderActions}
          signals={contactHeaderSignals}
          healthHistory={healthHistory}
          onDeleteRequest={() => setShowDeleteConfirm(true)}
        />

        <CrmContactQuickInfoGrid
          items={contactQuickInfoItems}
          onSave={handleInlineEdit}
          className="mt-4"
        />

        <CrmContactOwnerAssignmentCard
          visibleOwnerLabel={visibleOwnerLabel}
          ownerDraftId={ownerDraftId}
          memberOptions={memberOptions}
          isOwnerDraftKnown={isOwnerDraftKnown}
          hasOwnerDraftChanged={hasOwnerDraftChanged}
          isLoadingMembers={areOrganizationMembersLoading}
          membersError={organizationMembersError}
          isSavingOwner={isSavingOwner}
          feedback={ownerMutationFeedback}
          error={ownerMutationError}
          onOwnerDraftChange={(nextOwnerId) => {
            setOwnerDraftId(nextOwnerId);
            setOwnerMutationFeedback(null);
            setOwnerMutationError(null);
          }}
          onSaveOwner={() => { void handleSaveOwner(); }}
        />

        <CrmContactAiInsightsPanel
          summary={summary}
          isSummarizing={isSummarizing}
          suggestedStage={suggestedStage}
          suggestedConfidence={suggestedConfidence}
          isStageSuggestionLoading={isStageSuggestionLoading}
          suggestions={aiSuggestions}
          suggestionStatuses={aiSuggestionStatuses}
          isGeneratingSuggestions={aiSuggestionMutation.isLoading}
          error={contactAiInsightsError}
          onGenerateSummary={handleGenerateSummary}
          onSuggestStage={() => { void fetchSuggestion(); }}
          onGenerateSuggestions={() => { void handleGenerateAiSuggestions(); }}
          onSuggestionDecision={(suggestion, index, action) => { void handleAiSuggestionDecision(suggestion, index, action); }}
        />
      </div>

      <CrmContactTagsEditor
        tags={visibleTags}
        tagInput={tagInput}
        removingTag={removingTag}
        error={tagMutationError}
        onTagInputChange={setTagInput}
        onAddTag={handleAddTag}
        onRemoveTag={(tag) => { void handleRemoveTag(tag); }}
      />

      <CrmContactDetailTabs
        tabs={CONTACT_DETAIL_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "details" && (
          <div className="space-y-3 text-sm p-4">
            <CrmContactDetailFactsList
              items={[
                { label: "Email Verified", value: contact.emailVerifiedAt ? "Yes" : "No" },
                { label: "Timezone", value: contact.timezone ?? "Not set" },
                { label: "Engagement", value: contact.engagementStatus ?? "Unknown" },
                { label: "Created", value: formatDate(contact.createdAt) },
                { label: "Updated", value: formatDate(contact.updatedAt) },
              ]}
            />
            <CrmLeadScoreEvidenceCard
              breakdown={scoreBreakdown}
              meta={scoreBreakdownMeta}
              isLoading={isScoreBreakdownLoading}
              error={scoreBreakdownError}
              fallbackScore={typeof contact.leadScore === "number" ? contact.leadScore : null}
            />
            <CrmHealthSnapshotCard snapshot={contactHealthSnapshot} />
            <CrmProgressSnapshotCard snapshot={contactProgressSnapshot} />
            <RelatedTaskSnapshotCard
              title="Open CRM tasks"
              description="Read-only task snapshot for this contact. Open the full task workspace for guidance, source evidence, history, and completion."
              tasks={visibleContactTasks}
              openTaskCount={openContactTasks.length}
              isLoading={isContactTasksLoading}
              error={contactTasksError}
              viewAllHref={`/crm/tasks?personId=${encodeURIComponent(contact.id)}`}
              emptyMessage="No open tasks for this contact."
              overflowMessage={(visibleCount, totalCount) => `Showing ${visibleCount} of ${totalCount} open tasks for this contact.`}
              evidenceLabel="Contact task reusable evidence"
            />
            <CrmContactDuplicateScanCard
              result={duplicateScanResult}
              error={duplicateScanError}
              isScanning={isScanningDuplicates}
              onScan={() => { void handleDuplicateScan(); }}
            />
            <CrmSourceAttributionCard
              summary={attributionSummary.data}
              isLoading={attributionSummary.isLoading}
              error={attributionSummary.error}
            />
            <CrmCommunicationMemoryCard memory={communicationMemory.data} />
            <CrmOperatorBriefCard brief={contactOperatorBrief} />
            <CrmRecommendationHintsCard sets={contactRecommendationHints} />
            <CrmAssessmentResultsCard results={contactAssessmentResults} />
            <CrmIntakeAnswersCard sets={contactIntakeAnswers} />
            <CrmInteractionTimelineCard items={contactInteractionTimeline} />
                  <CrmProfileSectionsCard
                    sections={contactProfileSections.sections}
                    title="Profile intelligence"
                    description="Configured facts from this contact's product, service, commerce, and support history."
                    titleClassName="text-xs font-medium uppercase tracking-normal"
                  />
            <CrmContactActionGroupsCard groups={contactActionGroups} />
            <CrmContactBookingOverridesCard
              overrides={bookingOverrides}
              services={bookingServices}
              draft={overrideDraft}
              error={overrideError}
              isSaving={isSavingOverride}
              onDraftChange={setOverrideDraft}
              onNew={() => beginOverrideEdit()}
              onSave={() => { void handleSaveOverride(); }}
              onEdit={beginOverrideEdit}
              onDelete={(overrideId) => { void handleDeleteOverride(overrideId); }}
            />
            <CrmAdditionalProfileFieldsCard fields={contactProfileSections.remainingFields} />
          </div>
        )}
        {activeTab === "timeline" && (
          <div className="space-y-4 p-4">
            <SendFatigueTimeline contactId={contactId} />
            <CrmContactActivityLogCard
              draft={activityDraft}
              isLogging={isLoggingActivity}
              feedback={activityFeedback}
              error={activityError}
              onDraftChange={setActivityDraft}
              onLog={() => { void handleLogActivity(); }}
            />
            <UnifiedTimeline key={timelineRefreshKey} contactId={contactId} />
          </div>
        )}
        {activeTab === "relationships" && (
          <div className="p-4">
            <CrmRelationshipMap contactId={contactId} />
          </div>
        )}
        {activeTab === "enrichment" && (
          <div className="p-4">
            <EnrichmentPanel contactId={contactId} />
          </div>
        )}
        {activeTab === "health" && (
          <div className="p-4">
            <HealthScorePanel current={healthCurrent} history={healthHistory} />
          </div>
        )}
        {activeTab === "properties" && (
          <div className="p-4">
            <PropertyAssetTab contactId={contactId} />
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <CrmContactDeleteConfirmDialog
          displayName={displayName}
          isDeleting={isDeleting}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      )}
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
